/**
 * Modal component for previewing course-wide changes
 */

import React, { useEffect, useState } from 'react'
import { Modal, Button, Input, Icon } from 'antd'
import { useTranslation } from 'react-i18next'

const { TextArea } = Input

const PreviewAllModal = ({
  visible,
  onCancel,
  onApply,
  stagedContent,
  onStagedContentChange,
  onUndo
}) => {
  const [expandedTopics, setExpandedTopics] = useState([])

  useEffect(() => {
    if (visible && stagedContent) {
      setExpandedTopics(Object.keys(stagedContent))
    }
  }, [visible, stagedContent])
  const { t } = useTranslation()

  return (
    <Modal
      title={t('v5:review_course_wide_changes')}
      visible={visible}
      onCancel={onCancel}
      centered
      className="ai-preview-all-content"
      footer={[
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button key="undo" onClick={onUndo} className="shl-secondary-btn">
            {t('v4:undo')}
          </Button>
          <Button
            key="apply"
            onClick={async () => {
              if (onApply) {
                try {
                  await onApply()
                } catch (e) {
                  // swallow to still close only if desired
                }
              }
              if (onCancel) onCancel()
            }}
            className="shl-primary-btn"
            type="primary"
          >
            {t('v4:apply_changes')}
          </Button>
        </div>
      ]}
    >
      <div
        style={{
          marginBottom: 12,
          display: 'flex',
          justifyContent: 'flex-end'
        }}
      >
        <Button
          onClick={() => {
            if (!stagedContent) return
            if (expandedTopics.length) {
              setExpandedTopics([])
            } else {
              setExpandedTopics(Object.keys(stagedContent))
            }
          }}
        >
          {expandedTopics.length ? t('v5:collapse_all') : t('v5:expand_all')}
        </Button>
      </div>

      <div style={{ maxHeight: '60vh', overflow: 'auto' }}>
        {stagedContent &&
          Object.entries(stagedContent).map(([topic, subtopics]) => {
            const isOpen = expandedTopics.indexOf(topic) !== -1
            return (
              <div
                key={topic}
                style={{
                  marginBottom: 16,
                  border: '1px solid #e8e8e8',
                  borderRadius: 4
                }}
              >
                <div
                  onClick={() => {
                    setExpandedTopics(prev =>
                      prev.indexOf(topic) !== -1
                        ? prev.filter(t => t !== topic)
                        : [...prev, topic]
                    )
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 12px',
                    cursor: 'pointer',
                    background: '#fafafa',
                    borderBottom: isOpen ? '1px solid #e8e8e8' : 'none',
                    userSelect: 'none'
                  }}
                >
                  <h3 style={{ margin: 0 }}>{topic}</h3>
                  <Icon
                    type="down"
                    style={{
                      transition: 'transform 0.2s',
                      transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)'
                    }}
                  />
                </div>
                {isOpen && (
                  <div style={{ padding: 12 }}>
                    {Object.entries(subtopics).map(([sub, value]) => (
                      <div key={sub} style={{ marginBottom: 12 }}>
                        <strong>{sub}</strong>
                        <TextArea
                          rows={6}
                          value={(value && value.generatedContent) || ''}
                          onChange={e => {
                            if (!onStagedContentChange) return
                            const newText = e.target.value
                            const updated = {
                              ...stagedContent,
                              [topic]: {
                                ...stagedContent[topic],
                                [sub]: {
                                  ...(stagedContent[topic] &&
                                    stagedContent[topic][sub]),
                                  generatedContent: newText
                                }
                              }
                            }
                            onStagedContentChange(updated)
                          }}
                          style={{ marginTop: 6, resize: 'vertical' }}
                        />
                        <div
                          style={{
                            textAlign: 'right',
                            marginTop: 4,
                            color: 'rgba(0,0,0,0.45)'
                          }}
                        >
                          {(value &&
                            value.generatedContent &&
                            value.generatedContent.length) ||
                            0}{' '}
                          {t('v4:characters')}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
      </div>
    </Modal>
  )
}

export default PreviewAllModal
